/**
 * useAvailableTasks — list pending unassigned cleaning_tasks in the
 * current cleaner's org.
 *
 * Added 2026-05-18 for ABC SMT go-live: a cleaner with no assignment
 * needs a way to take a job themselves. RLS at
 * 20260518000001_self_claim_unassigned_tasks.sql widens the
 * cleaning_tasks_select policy to include this surface for the
 * 'cleaner' role; the explicit `eq` filters below keep the query plan
 * tight on busy orgs even though RLS would scope it anyway.
 *
 * Enabled only when the current user's role is 'cleaner' — supervisors
 * and admins use CreateTaskPage to assign rather than self-claim. Other
 * roles get an empty list and no network round-trip.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'
import { useAuthStore, selectRole, selectOrgId, selectUserId } from '@/store/authStore'

export interface AvailableTaskRow {
  id:         string
  room_id:    string
  cycle_type: string
  created_at: string
  room: {
    id:             string
    room_number:    string
    current_status: string
  } | null
}

export function useAvailableTasks(): UseQueryResult<AvailableTaskRow[]> {
  const supabase = useSupabase()
  const userId   = useAuthStore(selectUserId)
  const role     = useAuthStore(selectRole)
  const orgId    = useAuthStore(selectOrgId)

  return useQuery({
    queryKey: ['available-tasks', orgId, userId],
    enabled:  !!userId && !!orgId && role === 'cleaner',
    queryFn: async (): Promise<AvailableTaskRow[]> => {
      if (!userId || !orgId || role !== 'cleaner') return []
      const { data, error } = await supabase
        .from('cleaning_tasks')
        .select('id, room_id, cycle_type, created_at, room:rooms(id, room_number, current_status)')
        .eq('organization_id',    orgId)
        .eq('status',             'pending')
        .is('assigned_to_user_id', null)
        .order('created_at', { ascending: true })
        .limit(50)

      if (error) throw error
      return (data ?? []) as unknown as AvailableTaskRow[]
    },
  })
}
